import type { WorkflowDetailsResponse } from 'api/services/workflow/workflow.schema';
import type { Edge, Node } from '@xyflow/react';
import {
  ActionGraphNode,
  EnrollGraphNode,
  WaitGraphNode,
} from './map-workflow-details-to-canvas.types';
import type { FormValues } from '../workflow-sidebar/edit-node-form/nodes/enroll-node-editor/enroll-node-editor.types';
import { NodeType } from '../hooks/use-sidebar-control/use-sidebar-control.types';

const makeEdgeId = (source: string, target: string) => `e-${source}-${target}`;

const defaultNodeWidth = 320;
const defaultHorizontalMargin = 50;
const startX = 200;
const startY = 200;

function mapMappedEnrollToNodeData(mapped: EnrollGraphNode): FormValues {
  const orBlocks = mapped.rules.OR.map((or) => ({
    andBlocks: or.AND.map((and) => {
      if (and.key === 'branchOfficeIds') {
        return {
          entityType: 'branchOfficeIds' as const,
          condition: and.condition,
          entityId: and.value.map(mapEntityRefToOption),
        };
      }
      return {
        entityType: 'jobTypeIds' as const,
        condition: and.condition,
        entityId: and.value.map(mapEntityRefToOption),
      };
    }),
  }));

  return { orBlocks };
}

function mapMappedWaitToNodeData(mapped: WaitGraphNode): { percentage: number } {
  return { percentage: mapped.rules.value };
}

function mapEntityRefToOption(ref: { value: string; label: string }): {
  label: string;
  value: string;
} {
  return { label: ref.label, value: ref.value };
}

function mapMappedActionToNodeData(mapped: ActionGraphNode): {
  value: Array<{ label: string; value: string }>;
} {
  const value =
    'value' in mapped.rules && Array.isArray(mapped.rules.value) ? mapped.rules.value : [];

  return {
    value: value.map(mapEntityRefToOption),
  };
}

export function mapWorkflowDetailsToCanvas(graph: WorkflowDetailsResponse['graph']): {
  nodes: Node[];
  edges: Edge[];
} {
  if (graph.length === 0) {
    return { nodes: [], edges: [] };
  }

  let currentX = startX;
  const nodes: Node[] = graph.map((mapped) => {
    const position = { x: currentX, y: startY };
    currentX += defaultNodeWidth + defaultHorizontalMargin;

    if (mapped.type === NodeType.Enroll) {
      return {
        id: mapped.id,
        type: NodeType.Enroll,
        position,
        data: mapMappedEnrollToNodeData(mapped),
      };
    }
    if (mapped.type === NodeType.Wait) {
      return {
        id: mapped.id,
        type: NodeType.Wait,
        position,
        data: mapMappedWaitToNodeData(mapped),
      };
    }
    if (mapped.type === NodeType.Action) {
      return {
        id: mapped.id,
        type: NodeType.Action,
        position,
        data: mapMappedActionToNodeData(mapped),
      };
    }
    throw new Error('Invalid node type');
  });

  const edges: Edge[] = [];
  for (let i = 0; i < graph.length - 1; i++) {
    edges.push({
      id: makeEdgeId(graph[i].id, graph[i + 1].id),
      source: graph[i].id,
      target: graph[i + 1].id,
      type: 'smoothstep',
    });
  }

  return {
    nodes,
    edges,
  };
}
